import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  branchControllerForkBranchWithoutPolicy,
  branchControllerInvalidateBranch,
} from 'korint-sdk';
import { invalidatePolicyDashboardCache } from 'korint-sdk/react';
import { ArchiveIcon, ArchiveRestoreIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip/tooltip';
import { toast } from '@folio/components';

export function ToggleArchiveBranchButton() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    policy,
    branches,
    workingBranch,
    mutate: refetchPolicyOnDashboard,
  } = usePolicyOnDashboard();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isArchivable =
    (workingBranch?.branchType === 'NEW_BUSINESS' || workingBranch?.branchType === 'MTA') &&
    workingBranch?.branchStatus === 'BRANCH_OPEN' &&
    ['POLICY_CREATED', 'POLICY_QUOTED', 'POLICY_CONFIRMED'].includes(policy.status);

  const alreadyRestored = Boolean(
    branches?.data.some(
      branch =>
        branch.branchStatus === 'BRANCH_OPEN' && branch.parentBranchId === workingBranch?.id,
    ),
  );
  const isUnarchivable = workingBranch?.branchStatus === 'BRANCH_CLOSED' && !alreadyRestored;

  const hasPendingDerogation = Boolean(
    workingBranch?.derogations?.some(({ status }) => status === 'CREATED'),
  );

  const { mutate: archiveBranch, isPending: isArchiving } = useMutation({
    mutationFn: async () => {
      await branchControllerInvalidateBranch(policy.id, workingBranch!.id, {
        reason: 'BROKER_REQUEST',
      });
    },
    onSuccess: async () => {
      toast({ variant: 'default', title: t('archive.archiveSuccess') });
      await refetchPolicyOnDashboard();
      await invalidatePolicyDashboardCache(queryClient, policy.id);
    },
    onError: () => {
      toast({ variant: 'destructive', title: t('archive.archiveError') });
    },
  });

  const { mutate: unarchiveBranch, isPending: isUnarchiving } = useMutation({
    mutationFn: async () => {
      return branchControllerForkBranchWithoutPolicy(workingBranch!.id, {
        name: workingBranch!.name,
        closeOriginalBranch: false,
      });
    },
    onSuccess: async data => {
      toast({ variant: 'default', title: t('archive.unarchiveSuccess') });
      navigate(`/policies/${policy.id}/${data.id}`);
      await invalidatePolicyDashboardCache(queryClient, policy.id);
    },
    onError: () => {
      toast({ variant: 'destructive', title: t('archive.unarchiveError') });
    },
  });

  if (!isArchivable && !isUnarchivable) {
    return null;
  }

  const isPending = isArchiving || isUnarchiving;
  const label = isUnarchivable ? t('archive.unarchiveButton') : t('archive.archiveQuote');
  const icon = isUnarchivable ? (
    <ArchiveRestoreIcon className="h-4 w-4" />
  ) : (
    <ArchiveIcon className="h-4 w-4" />
  );

  const onClick = () => {
    if (isUnarchivable) {
      unarchiveBranch();
      return;
    }
    if (hasPendingDerogation) {
      setConfirmOpen(true);
      return;
    }
    archiveBranch();
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="icon" onClick={onClick} disabled={isPending}>
            {isPending ? <Spinner className="h-4 w-4 text-current" /> : icon}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('archive.pendingDerogationTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('archive.pendingDerogationDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('archive.pendingDerogationCancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={isArchiving} onClick={() => archiveBranch()}>
              {t('archive.pendingDerogationConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
